// src/core/telegram/TelegramContext.tsx
import React, { createContext, useContext } from 'react';
import { ITelegramWebApp } from './ITelegramWebApp';
import { usePlatform } from '../environment/PlatformContext';

const TelegramContext = createContext<ITelegramWebApp | null>(null);

export const TelegramProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isTelegram } = usePlatform();

  let tg: ITelegramWebApp;

  if (isTelegram && (window as any).Telegram?.WebApp) {
    // Приводим к any, затем к нашему интерфейсу,
    // потому что мы знаем, что реальный WebApp содержит нужные методы.
    const raw = (window as any).Telegram.WebApp as any;

    tg = {
      ready: () => raw.ready?.(),
      expand: () => raw.expand?.(),
      initData: raw.initData ?? '',
      colorScheme: raw.colorScheme ?? 'light',
      themeParams: {
        bg_color: raw.themeParams?.bg_color,
        text_color: raw.themeParams?.text_color,
        button_color: raw.themeParams?.button_color,
        secondary_bg_color: raw.themeParams?.secondary_bg_color,
      },
      MainButton: {
        show: () => raw.MainButton?.show(),
        hide: () => raw.MainButton?.hide(),
        setText: (text) => raw.MainButton?.setText(text),
        onClick: (cb) => raw.MainButton?.onClick(cb),
        offClick: (cb) => raw.MainButton?.offClick(cb),
        enable: () => raw.MainButton?.enable(),
        disable: () => raw.MainButton?.disable(),
      },
      BackButton: {
        show: () => raw.BackButton?.show(),
        hide: () => raw.BackButton?.hide(),
        onClick: (cb) => raw.BackButton?.onClick(cb),
        offClick: (cb) => raw.BackButton?.offClick(cb),
      },
      onEvent: (event, cb) => raw.onEvent?.(event, cb),
      offEvent: (event, cb) => raw.offEvent?.(event, cb),
      openInvoice: (url) => raw.openInvoice?.(url),
    };
  } else {
    // Заглушка для веба
    const noop = () => {};
    tg = {
      ready: noop,
      expand: noop,
      initData: '',
      colorScheme: 'light',
      themeParams: {},
      MainButton: {
        show: noop,
        hide: noop,
        setText: noop,
        onClick: noop,
        offClick: noop,
        enable: noop,
        disable: noop,
      },
      BackButton: {
        show: noop,
        hide: noop,
        onClick: noop,
        offClick: noop,
      },
      onEvent: noop,
      offEvent: noop,
      openInvoice: noop,
    };
  }

  return <TelegramContext.Provider value={tg}>{children}</TelegramContext.Provider>;
};

export const useTelegram = (): ITelegramWebApp => {
  const context = useContext(TelegramContext);
  if (!context) {
    throw new Error('useTelegram must be used within a TelegramProvider');
  }
  return context;
};